import React, { useContext, useEffect, useState } from "react";
import "./Dashboard.css";
import { Button, Col, Row } from "antd";
import axios from "axios";
import * as links from "../../../utils/links";
import { AuthContext } from "../../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import LoadingAction from "../../../themes/LoadingAction/LoadingAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCheckCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const normalizarDescricaoMaquina = (descricao) => {
  const texto = String(descricao || "").trim();
  if (!texto) return texto;
  return texto.replace(/^m[aá]quina\b/i, "Máquina");
};

const numeroDescricaoMaquina = (descricao) => {
  const match = String(descricao || "").match(/(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const ordenarMaquinasPorDescricao = (lista) =>
  [...(Array.isArray(lista) ? lista : [])]
    .map((item) => ({
      ...item,
      descricao: normalizarDescricaoMaquina(item?.descricao),
    }))
    .sort((a, b) => {
      const numeroA = numeroDescricaoMaquina(a?.descricao);
      const numeroB = numeroDescricaoMaquina(b?.descricao);

      if (numeroA !== numeroB) return numeroA - numeroB;

      return String(a?.descricao || "").localeCompare(
        String(b?.descricao || ""),
        "pt-BR",
        { numeric: true, sensitivity: "base" }
      );
    });

const statusVisualMaquina = (post) => {
  const statusServidor = String(post?.status || "").toUpperCase();

  if (statusServidor === "OFFLINE") return "OFFLINE";

  const ultimoPagamento = post?.ultimoPagamentoRecebido
    ? new Date(post.ultimoPagamentoRecebido).getTime()
    : NaN;
  const idadePagamento = Date.now() - ultimoPagamento;

  if (
    Number.isFinite(ultimoPagamento) &&
    idadePagamento >= 0 &&
    idadePagamento < 15 * 60 * 1000
  ) {
    return "PAGAMENTO_RECENTE";
  }

  if (statusServidor === "PAGAMENTO_RECENTE") return "PAGAMENTO_RECENTE";
  return "ONLINE";
};

const DashboardFornecedor = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const navigate = useNavigate();
  const token = dataUser?.token;
  const [totalFornecedores, setTotalFornecedores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!totalFornecedores?.length) return;
    const ultimaId = sessionStorage.getItem("ultimaMaquinaFornecedor");
    if (!ultimaId) return;
    let tentativas = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(`maquina-fornecedor-${ultimaId}`);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "center" });
        sessionStorage.removeItem("ultimaMaquinaFornecedor");
        clearInterval(timer);
      }
      tentativas += 1;
      if (tentativas >= 30) clearInterval(timer);
    }, 300);
    return () => clearInterval(timer);
  }, [totalFornecedores]);

  useEffect(() => {
    dataData();
    const intervalId = setInterval(dataData, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataData = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_SERVIDOR}/maquinas`, {
        headers: { "x-access-token": token, "content-type": "application/json" },
      })
      .then((res) => {
        if (res.status !== 200) throw new Error();
        setTotalFornecedores(ordenarMaquinasPorDescricao(res.data));
      })
      .catch((err) => {
        if ([401, 403].includes(err?.response?.status)) {
          setNotiMessage({ type: "error", message: "A sua sessão expirou, para continuar faça login novamente." });
          navigate(dataUser?.key === "ADMIN" ? links.ADMIN_SIGNIN : links.SIGNIN);
          setDataUser(null);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const abrirMaquina = (post) => {
    sessionStorage.setItem("ultimaMaquinaFornecedor", String(post.id));
    navigate(`${links.FORNECEDOR_SEARCH_CANAIS}/${post.id}`, {
      state: {
        nome: post.nome,
        store_id: post.store_id,
        pulso: post.pulso,
        estoque: post.estoque,
        descricao: normalizarDescricaoMaquina(post.descricao),
        maquininha_serial: post.maquininha_serial,
      },
    });
  };

  const sinalIcone = (post) => {
    if (statusVisualMaquina(post) === "OFFLINE" || !post.nivelDeSinal) {
      return <i className="bi bi-reception-0" style={{ fontSize: "42px", color: "transparent" }} />;
    }
    const nivel = Number(post.nivelDeSinal || 0);
    const classe = nivel <= 30 ? "bi-reception-1" : nivel <= 60 ? "bi-reception-2" : nivel <= 70 ? "bi-reception-3" : "bi-reception-4";
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-top-${post.id}`}>{nivel}%</Tooltip>}>
        <i className={`bi ${classe}`} style={{ fontSize: "42px", color: "#1687ff" }} />
      </OverlayTrigger>
    );
  };

  return (
    <div className="Dashboard_container">
      {isLoading && <LoadingAction />}
      {dataUser?.warningMsg && (
        <div className="WarningMsgSpan">
          <span>
            Seu plano está bloqueado, acesse <Link to={links.CLIENTE_MENSALIDADES} className="underline-link">Minhas Mensalidades</Link> para renovar.
          </span>
        </div>
      )}

      <div className="Dashboard_header">
        <div className="Dashboard_staBlockTitle">Minhas Máquinas</div>
      </div>

      <div className="Dashboard_action">
        <Button style={{ margin: "0 15px" }} onClick={dataData}>
          <FontAwesomeIcon icon={faArrowsRotate} style={{ marginRight: "5px" }} />
          Atualizar
        </Button>
      </div>

      <Row>
        {totalFornecedores.map((post) => {
          const statusVisual = statusVisualMaquina(post);
          const classeStatus = statusVisual === "OFFLINE"
            ? "status-offline"
            : statusVisual === "PAGAMENTO_RECENTE"
              ? "status-pagamento"
              : "status-online";

          return (
            <Col xs={24} md={24} lg={8} xl={8} className="Dashboard_col" id={`maquina-fornecedor-${post.id}`} key={post.id}>
              <div className="maquina" onClick={() => abrirMaquina(post)}>
                <div className="maquina-info">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <FontAwesomeIcon
                      icon={statusVisual === "OFFLINE" ? faXmarkCircle : faCheckCircle}
                      className={`logout-icon fa-3x ${classeStatus}`}
                    />
                    {sinalIcone(post)}
                  </div>
                  <h2>{post.nome}</h2>
                  <h6 style={{ fontWeight: "300" }}>{statusVisual} - {normalizarDescricaoMaquina(post.descricao)}</h6>
                  <h6 style={{ fontWeight: "300" }}>MP: {post.store_id}</h6>
                  <h6 style={{ fontWeight: "300" }}>PAG: {post.maquininha_serial}</h6>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default DashboardFornecedor;
